import { useCallback } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { type Address } from '@dhl/components'
import {
  type Carrier,
  useGetAddressesByCarrierCountryAndPostalCodeHook,
  useGetAddressByCarrierCountryPostalCodeAndHouseNumberHook,
  type AddressesLookupResponse,
  getGetAddressesByCarrierCountryAndPostalCodeQueryKey,
  getGetAddressByCarrierCountryPostalCodeAndHouseNumberQueryKey,
} from '@dhl/receiver-address-format-api'

type AddressDataLookupFn = (
  carrier: Carrier,
  country: string,
  values: Partial<Address>
) => Promise<AddressesLookupResponse[]>

type AddressDataLookupResult = {
  lookupByPostalCode: AddressDataLookupFn
  lookupByPostalCodeAndHouseNumber: AddressDataLookupFn
}

export const useAddressDataLookup = (): AddressDataLookupResult => {
  const getByPostalCode = useGetAddressesByCarrierCountryAndPostalCodeHook()
  const getByPostalCodeAndHouseNumber = useGetAddressByCarrierCountryPostalCodeAndHouseNumberHook()
  const queryClient = useQueryClient()

  const lookupByPostalCode = useCallback(
    (carrier: Carrier, country: string, values: Address) =>
      queryClient.fetchQuery({
        queryKey: [
          ...getGetAddressesByCarrierCountryAndPostalCodeQueryKey(carrier, country, values.postalCode),
          getByPostalCode,
        ],
        queryFn: () => getByPostalCode(carrier, country, values.postalCode),
        staleTime: 1000 * 60 * 10,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- getByPostalCode is not a stable reference
    [queryClient]
  )

  const lookupByPostalCodeAndHouseNumber = useCallback(
    (carrier: Carrier, country: string, values: Address) =>
      queryClient.fetchQuery({
        queryKey: [
          ...getGetAddressByCarrierCountryPostalCodeAndHouseNumberQueryKey(
            carrier,
            country,
            values.postalCode,
            parseInt(values.houseNumber, 10)
          ),
          getByPostalCodeAndHouseNumber,
        ],
        queryFn: () =>
          getByPostalCodeAndHouseNumber(carrier, country, values.postalCode, parseInt(values.houseNumber, 10)),
        staleTime: 1000 * 60 * 10,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- getByPostalCodeAndHouseNumber is not a stable reference
    [queryClient]
  )

  return {
    lookupByPostalCode,
    lookupByPostalCodeAndHouseNumber,
  }
}
